/**
 * Analytics Page Router Component
 *
 * Handles routing between main analytics page and zone details view
 */

import React from 'react'
import { Routes, Route } from 'react-router-dom'
import AnalyticsMain from './analytics-main'
import ZoneDetailsView from './components/zone-details-view'
import type { RecordModalState, DoneModalState } from './types'
import RecordValueModal from 'src/components/shared/record-value-modal'
import WorkerValueSummaryModal from 'src/components/shared/worker-value-summary-modal'
import DoneLocationModal from 'src/components/shared/done-location-modal'
import { v4 as uuidv4 } from 'uuid'
import { useSyncedStore } from '@syncedstore/react'
import {
  todoStore,
  addWorkerAssignmentLog,
  softDeleteWorkerAssignmentLog,
  getDoc,
  getClientId,
} from 'src/store/todo'
import { getWorkerValueSummary } from './utils/filtering'
import type { SummaryModalState } from './types'

const STORAGE_KEY = 'activities_selected_team'

/**
 * Analytics Page Router
 *
 * Provides routing structure:
 * - /analytics → Main page (grid/analytics views)
 * - /analytics/zone/:zoneId → Zone details view
 */
const AnalyticsPage: React.FC = () => {
  const [selectedTeamId] = React.useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY)
  })
  const [recordModalState, setRecordModalState] = React.useState<RecordModalState>(null)
  const [summaryModalState, setSummaryModalState] = React.useState<SummaryModalState>(null)
  const [doneModalState, setDoneModalState] = React.useState<DoneModalState>(null)
  const [deleteConfirmLogId, setDeleteConfirmLogId] = React.useState<string | null>(null)

  const state = useSyncedStore(todoStore)

  /**
   * Handle saving a recorded value
   */
  const handleSaveRecordedValue = (value: number) => {
    if (recordModalState && selectedTeamId) {
      try {
        addWorkerAssignmentLog(
          selectedTeamId,
          recordModalState.workerId,
          recordModalState.taskTypeId,
          recordModalState.zoneId,
          value,
          recordModalState.locationId
        )
        const assignment = state.workerAssignments.find(
          (a) =>
            a.workerId === recordModalState.workerId &&
            a.taskTypeId === recordModalState.taskTypeId &&
            a.zoneId === recordModalState.zoneId &&
            a.locationId === recordModalState.locationId &&
            !a.softDeleted
        )
        if (assignment && !assignment.completed) {
          assignment.completed = true
        }
        setRecordModalState(null)
      } catch (error) {
        console.error('Error saving recorded value:', error)
      }
    }
  }

  /**
   * Handle confirming zone completion with selected locations
   */
  const handleConfirmZoneDone = (selectedLocationIds: string[], completionsToDelete: string[]) => {
    if (doneModalState && selectedTeamId) {
      try {
        const existingCompletions = state.workerAssignmentLogs.filter(
          (log: any) =>
            log.workerId === doneModalState.workerId &&
            log.taskTypeId === doneModalState.taskTypeId &&
            log.zoneId === doneModalState.zoneId &&
            log.locationId &&
            !log.softDeleted
        )

        const existingLocationIds = existingCompletions.map((c: any) => c.locationId)

        completionsToDelete.forEach((logId) => {
          softDeleteWorkerAssignmentLog(logId)
        })

        const batchTimestamp = Date.now()
        selectedLocationIds.forEach((locationId) => {
          if (!existingLocationIds.includes(locationId)) {
            const doc = getDoc()
            if (doc) {
              const newLog = {
                id: uuidv4(),
                workerId: doneModalState.workerId,
                teamId: selectedTeamId,
                taskTypeId: doneModalState.taskTypeId,
                zoneId: doneModalState.zoneId,
                locationId,
                value: 1,
                timestamp: batchTimestamp,
                clientId: getClientId()?.toString() || null,
              }
              doc.transact(() => {
                state.workerAssignmentLogs.push(newLog)
              })
            }
          }
        })

        setDoneModalState(null)
      } catch (error) {
        console.error('Error confirming zone completion:', error)
      }
    }
  }

  /**
   * Handle confirming entire zone completion
   */
  const handleConfirmZoneAll = () => {
    if (doneModalState && selectedTeamId) {
      try {
        const doc = getDoc()
        if (doc) {
          const newLog = {
            id: uuidv4(),
            workerId: doneModalState.workerId,
            teamId: selectedTeamId,
            taskTypeId: doneModalState.taskTypeId,
            zoneId: doneModalState.zoneId,
            value: 1,
            timestamp: Date.now(),
            clientId: getClientId()?.toString() || null,
          }
          doc.transact(() => {
            state.workerAssignmentLogs.push(newLog)
          })
        }
        setDoneModalState(null)
      } catch (error) {
        console.error('Error confirming zone completion:', error)
      }
    }
  }

  /**
   * Handle canceling zone completion
   */
  const handleCancelZoneCompletion = (completionId: string) => {
    try {
      softDeleteWorkerAssignmentLog(completionId)
      setDoneModalState(null)
    } catch (error) {
      console.error('Error canceling zone completion:', error)
    }
  }

  return (
    <>
      <Routes>
        <Route index element={<AnalyticsMain />} />
        <Route
          path="zone/:zoneId"
          element={
            <ZoneDetailsView
              selectedTeamId={selectedTeamId || ''}
              onOpenRecordModal={setRecordModalState}
              onOpenDoneModal={setDoneModalState}
              onSetDeleteConfirm={setDeleteConfirmLogId}
            />
          }
        />
      </Routes>

      {/* Shared Modals */}
      {recordModalState && (
        <RecordValueModal
          isOpen={recordModalState.isOpen}
          title="# of Boxes"
          onSave={handleSaveRecordedValue}
          onCancel={() => setRecordModalState(null)}
        />
      )}

      {summaryModalState && (
        <WorkerValueSummaryModal
          isOpen={summaryModalState.isOpen}
          workerName={summaryModalState.workerName}
          taskName={summaryModalState.taskName}
          summaryData={getWorkerValueSummary(
            state.workerAssignmentLogs,
            state.zones || [],
            state.locations || [],
            summaryModalState.workerId,
            summaryModalState.taskTypeId
          )}
          onClose={() => setSummaryModalState(null)}
        />
      )}

      {doneModalState && (() => {
        const zoneCompletion = state.workerAssignmentLogs.find(
          (log: any) =>
            log.workerId === doneModalState.workerId &&
            log.taskTypeId === doneModalState.taskTypeId &&
            log.zoneId === doneModalState.zoneId &&
            !log.locationId &&
            !log.softDeleted
        )

        return (
          <DoneLocationModal
            isOpen={doneModalState.isOpen}
            zone={{
              id: doneModalState.zoneId,
              zone_moniker: doneModalState.zoneName,
              locations: state.locations
                .filter((l: any) => l.zoneId === doneModalState.zoneId && l.isActive)
                .map((l: any) => ({
                  id: l.id,
                  location_moniker: l.locationMoniker,
                })),
            }}
            workerName={doneModalState.workerName}
            taskName={doneModalState.taskName}
            existingCompletions={state.workerAssignmentLogs
              .filter(
                (log: any) =>
                  log.workerId === doneModalState.workerId &&
                  log.taskTypeId === doneModalState.taskTypeId &&
                  log.zoneId === doneModalState.zoneId &&
                  log.locationId &&
                  !log.softDeleted
              )
              .map((log: any) => ({
                id: log.id,
                locationId: log.locationId,
              }))}
            existingZoneCompletion={zoneCompletion ? { id: zoneCompletion.id } : undefined}
            onConfirm={handleConfirmZoneDone}
            onConfirmZone={handleConfirmZoneAll}
            onCancelZone={handleCancelZoneCompletion}
            onCancel={() => setDoneModalState(null)}
          />
        )
      })()}

      {deleteConfirmLogId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-xs mx-4">
            <div className="p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Delete Record?</h3>
              <p className="text-sm text-gray-600 mb-6">
                This record will be marked as deleted but kept for audit purposes.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setDeleteConfirmLogId(null)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    softDeleteWorkerAssignmentLog(deleteConfirmLogId)
                    setDeleteConfirmLogId(null)
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AnalyticsPage
